import { materializeSession } from "~/hooks/media/durable-audio-recorder-db";
import {
    cleanupUploadedLocalRecording,
    type LocalCleanupOutcome,
} from "~/hooks/media/pending-local-cleanup";
import {
    getRecoverableRecordingKey,
    notifyRecoverableRecordingsChanged,
    type RecoverableRecording,
} from "~/hooks/media/recoverable-recordings";
import { logger } from "~/lib/logger";
import {
    deleteRecording as deleteLegacyRecording,
    getRecording as getLegacyRecording,
} from "~/lib/recording-storage";
import { base64ToBlob } from "~/lib/utils";

// AIDEV-NOTE: Local-only actions for stranded recordings (unfinished-uploads
// modal). Discard permanently deletes audio from this browser. UI callers must
// confirm first unless reconciliation proved an exact uploaded server twin.
// Download hands the raw audio file to the user (e.g. to trim an accidental
// all-day recording and re-upload via media upload).

// AIDEV-NOTE: Durable discards go through the bounded pending-cleanup path.
// On WebKit (Safari, every iOS browser) the readwrite chunk delete can stall
// indefinitely; an unbounded await here left the "Remove local copy" button
// spinning for 5+ min with no toast (Katie H., Sep 2026). A timed-out/failed
// delete is tracked in localStorage, hidden from the recovery list, and
// retried on the next listing — so the outcome is "gone from the UI" either
// way, and callers only need to phrase the toast.
export async function discardRecoverableRecording(
    recording: RecoverableRecording,
    options: { notify?: boolean } = {},
): Promise<LocalCleanupOutcome> {
    let outcome: LocalCleanupOutcome = "deleted";
    if (recording.kind === "durable") {
        outcome = await cleanupUploadedLocalRecording(recording.id);
    } else {
        await deleteLegacyRecording(recording.id);
        // AIDEV-NOTE: legacy deleteRecording swallows storage errors (logs
        // only) — verify the entry is actually gone so the UI can't confirm
        // a discard that didn't happen.
        const remaining = await getLegacyRecording(recording.id);
        if (remaining) {
            throw new Error(
                "Legacy recording still present after delete attempt",
            );
        }
    }
    logger
        .withMetadata({
            recordingKey: getRecoverableRecordingKey(recording),
            outcome,
        })
        .info("Discarded stranded local recording");
    if (options.notify !== false) notifyRecoverableRecordingsChanged();
    return outcome;
}

async function materializeRecordingBlob(
    recording: RecoverableRecording,
): Promise<{ blob: Blob; mimeType: string } | null> {
    if (recording.kind === "durable") {
        const materialized = await materializeSession(recording.id);
        if (!materialized?.blob || materialized.blob.size === 0) return null;
        return {
            blob: materialized.blob,
            mimeType:
                materialized.session.mimeType ||
                recording.mimeType ||
                "audio/webm",
        };
    }

    const legacy = await getLegacyRecording(recording.id);
    if (!legacy?.recordDataBase64) return null;
    const mimeType = legacy.mimeType || recording.mimeType || "audio/webm";
    return { blob: base64ToBlob(legacy.recordDataBase64, mimeType), mimeType };
}

/**
 * Object-URL playback source for a stranded local recording. Caller owns the
 * URL and must revoke it (URL.revokeObjectURL) when done.
 */
export async function getRecoverableRecordingPlaybackSource(
    recording: RecoverableRecording,
): Promise<{ url: string; mimeType: string } | null> {
    const materialized = await materializeRecordingBlob(recording);
    if (!materialized) return null;
    return {
        url: URL.createObjectURL(materialized.blob),
        mimeType: materialized.mimeType,
    };
}

function extensionFromMimeType(mimeType: string): string {
    return ((mimeType || "audio/webm").split("/")[1] || "bin").split(";")[0];
}

/** Returns false when the recording's audio can't be materialized. */
export async function downloadRecoverableRecording(
    recording: RecoverableRecording,
): Promise<boolean> {
    const materialized = await materializeRecordingBlob(recording);
    if (!materialized) return false;

    const createdDate = new Date(recording.createdAt);
    const datePart = Number.isNaN(createdDate.getTime())
        ? "unknown-date"
        : createdDate.toISOString().slice(0, 16).replace(/[:T]/g, "-");
    const fileName = `session-recording-${datePart}.${extensionFromMimeType(
        materialized.mimeType,
    )}`;

    const url = URL.createObjectURL(materialized.blob);
    try {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    } finally {
        // AIDEV-NOTE: Delay revoke — revoking synchronously can abort the
        // download of large blobs in some browsers.
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return true;
}
